import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import GlobalCursor from '../components/GlobalCursor'
import { nowAPI } from '../lib/api.js'

export default function Now() {
  const [nowData, setNowData] = useState({
    title: "what i'm doing now",
    content: "Currently working on exciting projects and always open to new opportunities."
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Load now data from API
    const loadNowData = async () => {
      try {
        const response = await nowAPI.get()
        if (response.data && response.data.length > 0) {
          setNowData(response.data[0])
        }
      } catch (error) {
        console.error('Error loading now data:', error)
        // Keep default data on error
      } finally {
        setLoading(false)
      }
    }
    
    loadNowData()
  }, [])

  // Listen for localStorage changes to refresh now data
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'helloheman_now' || e.type === 'nowDataUpdated') {
        const loadNowData = async () => {
          try {
            const response = await nowAPI.get()
            if (response.data && response.data.length > 0) {
              setNowData(response.data[0])
            }
          } catch (error) {
            console.error('Error loading now data:', error)
          }
        }
        loadNowData()
      }
    }

    const handleCustomEvent = (e) => {
      if (e.detail?.key === 'helloheman_now') {
        const loadNowData = async () => {
          try {
            const response = await nowAPI.get()
            if (response.data && response.data.length > 0) {
              setNowData(response.data[0])
            }
          } catch (error) {
            console.error('Error loading now data:', error)
          }
        }
        loadNowData()
      }
    }

    // Listen for localStorage changes (cross-tab)
    window.addEventListener('storage', handleStorageChange)
    
    // Listen for custom events (same-tab)
    window.addEventListener('nowDataUpdated', handleCustomEvent)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('nowDataUpdated', handleCustomEvent)
    }
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E06100]"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-black">
      <GlobalCursor />
      <div className="absolute inset-0 stars"></div>
      <div className="absolute inset-0 stars2"></div>

      <div className="relative z-20 px-4 sm:px-6 py-6 sm:py-8 max-w-6xl mx-auto pt-24 sm:pt-32 md:pt-40">
        {/* Page Header */}
        <div className="mb-12 text-center">
          <motion.div
            className="text-gray-400 text-xs sm:text-sm mb-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {'// now'}
          </motion.div>
          <motion.h1
            className="text-4xl md:text-5xl font-bold mb-1 text-white font-monument-extended"
            style={{
              fontFamily: "'Clash Display', 'Arial Black', sans-serif",
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {nowData.title}
          </motion.h1>
          <motion.div
            className="text-[#E06100] text-xs sm:text-sm uppercase font-semibold"
            style={{ letterSpacing: '2px' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Current focus & activities
          </motion.div>
        </div>

        {/* Main Content Container */}
        <div className="bg-black border border-gray-800 rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-2xl">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-white leading-relaxed space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.6,
                ease: 'easeOut',
              }}
            >
              <div className="prose prose-invert prose-lg max-w-none">
                <p className="text-xl sm:text-2xl font-medium text-white mb-6 leading-relaxed">
                  {nowData.content}
                </p>
                
                {/* Additional structured content if available */}
                {nowData.sections && nowData.sections.length > 0 && (
                  <div className="space-y-8">
                    {nowData.sections.map((section, index) => (
                      <div key={index} className="border-l-2 border-[#E06100] pl-6">
                        <h3 className="text-lg font-semibold text-white mb-3">{section.title}</h3>
                        <p className="text-gray-300 leading-relaxed">{section.content}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Call to action */}
                <div className="mt-12 pt-8 border-t border-gray-800">
                  <p className="text-gray-400 text-center">
                    Want to know more?{' '}
                    <a 
                      href="/connect" 
                      className="text-[#E06100] hover:text-white transition-colors underline"
                    >
                      Let's connect
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}